import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as businessVerificationApi from '../../api/businessVerification';
import { message } from 'antd';

// 获取企业认证列表
export const fetchVerificationList = createAsyncThunk(
  'businessVerification/fetchVerificationList',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await businessVerificationApi.getVerificationList(params);
      if (response && response.code === 200) {
        // 返回数据和查询参数，以便在reducer中保存
        return {
          data: response.data,
          params
        };
      }
      return rejectWithValue(response?.message || '获取企业认证列表失败');
    } catch (error) {
      return rejectWithValue(error.message || '获取企业认证列表失败');
    }
  }
);

// 获取企业认证审核队列
export const fetchQueueList = createAsyncThunk(
  'businessVerification/fetchQueueList',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await businessVerificationApi.getQueueList(params);
      if (response && response.code === 200) {
        // 返回数据和查询参数，以便在reducer中保存
        return {
          data: response.data,
          params
        };
      }
      return rejectWithValue(response?.message || '获取企业认证审核队列失败');
    } catch (error) {
      return rejectWithValue(error.message || '获取企业认证审核队列失败');
    }
  }
);

// 审核通过企业认证
export const approveVerification = createAsyncThunk(
  'businessVerification/approveVerification',
  async ({ id, operatorId }, { rejectWithValue }) => {
    try {
      const response = await businessVerificationApi.approveVerification(id, operatorId);
      if (response && response.code === 200) {
        message.success('审核通过成功');
        return response.data;
      }
      return rejectWithValue(response?.message || '审核通过失败');
    } catch (error) {
      return rejectWithValue(error.message || '审核通过失败');
    }
  }
);

// 拒绝企业认证
export const rejectVerification = createAsyncThunk(
  'businessVerification/rejectVerification',
  async ({ id, rejectReason, operatorId }, { rejectWithValue }) => {
    try {
      const response = await businessVerificationApi.rejectVerification(id, rejectReason, operatorId);
      if (response && response.code === 200) {
        message.success('拒绝认证成功');
        return response.data;
      }
      return rejectWithValue(response?.message || '拒绝认证失败');
    } catch (error) {
      return rejectWithValue(error.message || '拒绝认证失败');
    }
  }
);

// 初始状态
const initialState = {
  verificationList: {
    data: [],
    total: 0,
    current: 1,
    size: 10,
    loading: false,
    error: null,
    searchParams: {
      businessName: '',
      businessLicenseNo: '',
      legalPersonName: '',
      verifyStatus: undefined,
      businessType: undefined,
      sortBy: 'createdAt',
      sortDirection: 'desc'
    }
  },
  queueList: {
    data: [],
    total: 0,
    current: 1,
    size: 10,
    loading: false,
    error: null,
    searchParams: {
      verificationId: undefined,
      userId: undefined,
      status: undefined,
      reviewType: undefined,
      reviewerId: undefined,
      reviewResult: undefined
    }
  },
  operationStatus: {
    loading: false,
    success: false,
    error: null
  }
};

// 创建slice
const businessVerificationSlice = createSlice({
  name: 'businessVerification',
  initialState,
  reducers: {
    clearOperationStatus: (state) => {
      state.operationStatus = {
        loading: false,
        success: false,
        error: null
      };
    },
    setVerificationSearchParams: (state, action) => {
      state.verificationList.searchParams = {
        ...state.verificationList.searchParams,
        ...action.payload
      };
    },
    setQueueSearchParams: (state, action) => {
      state.queueList.searchParams = {
        ...state.queueList.searchParams,
        ...action.payload
      };
    }
  },
  extraReducers: (builder) => {
    builder
      // 处理获取企业认证列表
      .addCase(fetchVerificationList.pending, (state) => {
        state.verificationList.loading = true;
        state.verificationList.error = null;
      })
      .addCase(fetchVerificationList.fulfilled, (state, action) => {
        state.verificationList.loading = false;
        state.verificationList.data = action.payload.data.records || [];
        state.verificationList.total = action.payload.data.total || 0;
        state.verificationList.current = action.payload.data.current || 1;
        state.verificationList.size = action.payload.data.size || 10;
        
        // 保存搜索参数
        if (action.payload.params) {
          state.verificationList.searchParams = {
            ...state.verificationList.searchParams,
            ...action.payload.params
          };
        }
      })
      .addCase(fetchVerificationList.rejected, (state, action) => {
        state.verificationList.loading = false;
        state.verificationList.error = action.payload;
      })

      // 处理获取企业认证审核队列
      .addCase(fetchQueueList.pending, (state) => {
        state.queueList.loading = true;
        state.queueList.error = null;
      })
      .addCase(fetchQueueList.fulfilled, (state, action) => {
        state.queueList.loading = false;
        state.queueList.data = action.payload.data.records || [];
        state.queueList.total = action.payload.data.total || 0;
        state.queueList.current = action.payload.data.current || 1;
        state.queueList.size = action.payload.data.size || 10;
        
        // 保存搜索参数
        if (action.payload.params) {
          state.queueList.searchParams = {
            ...state.queueList.searchParams,
            ...action.payload.params
          };
        }
      })
      .addCase(fetchQueueList.rejected, (state, action) => {
        state.queueList.loading = false;
        state.queueList.error = action.payload;
      })

      // 处理审核通过
      .addCase(approveVerification.pending, (state) => {
        state.operationStatus.loading = true;
        state.operationStatus.success = false;
        state.operationStatus.error = null;
      })
      .addCase(approveVerification.fulfilled, (state) => {
        state.operationStatus.loading = false;
        state.operationStatus.success = true;
      })
      .addCase(approveVerification.rejected, (state, action) => {
        state.operationStatus.loading = false;
        state.operationStatus.error = action.payload;
        message.error(action.payload || '审核通过失败');
      })

      // 处理拒绝认证
      .addCase(rejectVerification.pending, (state) => {
        state.operationStatus.loading = true;
        state.operationStatus.success = false;
        state.operationStatus.error = null;
      })
      .addCase(rejectVerification.fulfilled, (state) => {
        state.operationStatus.loading = false;
        state.operationStatus.success = true;
      })
      .addCase(rejectVerification.rejected, (state, action) => {
        state.operationStatus.loading = false;
        state.operationStatus.error = action.payload;
        message.error(action.payload || '拒绝认证失败');
      });
  }
});

export const { 
  clearOperationStatus,
  setVerificationSearchParams,
  setQueueSearchParams
} = businessVerificationSlice.actions;

export default businessVerificationSlice.reducer; 